/*
 * Description:
 * ---------------------
 * Every word of the Firewall screen's Intrusions section (FirewallIntrusions)
 * and the Threat Intelligence screen (ThreatIntelPanel): what each jail
 * catches, how risky a ban looks, the fleet's shared list, and a person's way
 * back in when their own address was banned. Pure functions of what the box
 * (cloud-anchor/pkg/jail) and the server (server/pkg/fleetbans) answer.
 *
 * ! file components/intrusionWords.js
 */
import { count, plural } from "./firewallWords";
import { fmtDuration } from "../utils/formatters";

/* The built-in jails in plain words: a short name and what it catches. A
   custom jail is named by the operator and says what it catches itself. */
const JAILS = {
  sshd: { name: "Command line", catches: "wrong passwords at the box's command line (SSH)" },
  console: { name: "Console sign-in", catches: "wrong passwords at the box's own console" },
  api: { name: "Settings from outside", catches: "asking for the box's settings from the internet, which it always refuses" },
  portscan: { name: "Port scans", catches: "trying port after port the box keeps closed (needs the firewall on)" },
  slow: { name: "Slow guessing", catches: "one address failing again and again across a day, too slowly for the others to catch" },
  manual: { name: "Banned by hand", catches: "an operator's ban" },
};

export const jailName = (j) => JAILS[j?.name ?? j]?.name || j?.name || j;

/* A phrase made a sentence's start. */
export const capital = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);
export const jailCatches = (j) => JAILS[j?.name]?.catches || j?.about || `failures in ${sourceWords(j?.source)}`;

/* Where a custom jail reads its log, as a person says it. */
export function sourceWords(src) {
  if (!src) return "a log";
  if (src.unit) return `the ${src.unit.replace(/\.service$/, "")} service's log`;
  if (src.identifier) return `messages tagged ${src.identifier}`;
  if (src.file) return src.file;
  return "a log";
}

/* A duration in seconds as people say it: minutes and hours spelled out, and
   weeks and months once a span reaches them, so a month's ban is a month. */
export function span(s) {
  const n = Number(s || 0);
  if (n >= 7 * 86400) return fmtDuration(n);
  if (n % 86400 === 0 && n >= 86400) return plural(n / 86400, "day");
  if (n % 3600 === 0 && n >= 3600) return plural(n / 3600, "hour");
  if (n % 60 === 0) return plural(n / 60, "minute");
  return plural(n, "second");
}

/* A jail's rule as one sentence. */
export const ruleWords = (j) =>
  `${plural(j.max_retry, "failure")} within ${span(j.find_s)} bans an address for ${span(j.ban_s)}, longer when it looks riskier.`
  + (j.decoy ? ` ${EDITOR.decoyNote}` : "");

/* How risky a ban looks: a label and a tone of the screen's own (calm,
   look, bad). */
export function riskWords(b) {
  switch (b.tier) {
    case "permanent": return { label: "Banned for good", tone: "bad" };
    case "manual": return { label: "Banned by hand", tone: "look" };
    case "severe": return { label: "Severe: an attack", tone: "bad" };
    case "high": return { label: "High risk", tone: "bad" };
    case "medium": return { label: "Medium risk", tone: "look" };
    default: return { label: "Low risk, likely a typo", tone: "calm" };
  }
}

const at = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const today = d.toDateString() === new Date().toDateString();
  return d.toLocaleString([], today
    ? { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }
    : { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
};

/* When a ban ends, from now. */
export function untilWords(b, now = Date.now()) {
  if (!b.until) return "for good";
  const left = (new Date(b.until).getTime() - now) / 1000;
  if (left < 3600) return `for ${plural(Math.max(1, Math.round(left / 60)), "more minute")}`;
  if (left < 86400) return `until ${at(b.until)}`;
  return `until ${at(b.until)}`;
}

/* What a banned address did, in a few words. */
export function didWords(b) {
  if (b.manual) return (b.reasons || [])[0] || "banned by an operator";
  const tried = b.users > 1 ? `, ${b.users} user names` : "";
  return `${plural(b.failures, "failure")}${tried}`;
}

export const SECTION = {
  title: "Intrusion prevention",
  unavailable: "This box's build is older than intrusion prevention. Once it is updated, this fills in on its own.",
  unreachable: "Could not read intrusion prevention from the box. The screen keeps trying.",
};

/* The lead sentence: what is banned and what is watched. */
export function lead(st) {
  const bans = (st.bans || []).length;
  const on = (st.jails || []).filter((j) => j.enabled);
  const custom = on.filter((j) => !j.builtin).length;
  const stopped = Object.values(st.dropped || {}).reduce((a, n) => a + Number(n || 0), 0);
  const watching = on.length
    ? `It watches ${on.filter((j) => j.builtin).map((j) => jailName(j).toLowerCase()).join(", ")}${custom ? ` and ${plural(custom, "log")} of your own` : ""}.`
    : "Every jail is switched off, so nothing is watched.";
  return `${bans ? `${plural(bans, "address", "addresses")} banned now.` : "Nothing is banned now."} ${watching} ${count(stopped)} new connections from banned addresses stopped since the box started.`;
}

export const INTRO =
  "An address that keeps failing to sign in, or tries port after port, is banned for a while: a likely typo for minutes, an attack for a month. Only new connections from it are stopped. This network, the Weavian cloud and your management networks are never shut out for long.";

export const BANS = {
  title: "Banned now",
  none: "Nothing is banned. That is normal for a box the internet cannot reach.",
  cols: { address: "Address", did: "What it did", risk: "Risk", until: "Ends", actions: "" },
  why: "Why",
  unban: "Let back in",
  ask: "Ask Ada about this address",
  add: "Ban an address",
  unbanAsk: (b) => ({
    title: `Let ${b.address} back in?`,
    message: "It can connect to this box and your network again at once. If it keeps failing, it is banned again.",
    confirmLabel: "Let back in",
  }),
  trust: "and keep it from being banned again for a day",
  question: (b) => `Explain this ban in plain words, and whether to lift it: ${b.address}, banned by the ${b.jail} jail at ${b.at} with a risk score of ${b.score} (${b.tier}); reasons: ${(b.reasons || []).join("; ")}.`,
};

export const MANUAL = {
  title: "Ban an address",
  address: "Address",
  placeholder: "e.g. 203.0.113.9",
  howLong: "For",
  lengths: [[3600, "1 hour"], [86400, "1 day"], [604800, "1 week"], [2592000, "1 month"], [0, "good"]],
  reason: "Why",
  reasonPlaceholder: "So whoever reads this later knows",
  save: "Ban",
  cancel: "Cancel",
};

export const JAILS_CARD = {
  title: "What it watches",
  means: "Each jail counts failures of one kind, per address. Switch one off and it stops counting; the bans it made stay until they end.",
  banned: (n) => (n ? `${plural(n, "address", "addresses")} banned` : ""),
  failing: (n) => (n ? `counting ${plural(n, "address", "addresses")}` : ""),
  edit: "Change",
  remove: "Remove",
  add: "Watch another log",
  removeAsk: (j) => ({ title: `Stop watching ${jailName(j)}?`, message: "The bans it made stay until they end.", confirmLabel: "Remove" }),
};

export const EDITOR = {
  titleNew: "Watch another log",
  titleEdit: (j) => `Change ${jailName(j)}`,
  intro: "Point a jail at a log on this box and say what a failed sign-in looks like, or start from a ready-made filter for a common service.",
  name: "Name",
  namePlaceholder: "e.g. nginx-login",
  about: "What it catches",
  aboutPlaceholder: "e.g. wrong passwords at the shop's web store",
  preset: "Start from",
  presetNone: "My own patterns",
  where: "Where its log is",
  whereKinds: [["unit", "A service on this box"], ["file", "A file under /var/log"], ["identifier", "Messages tagged"]],
  wherePlaceholders: { unit: "e.g. nginx.service", file: "e.g. /var/log/nginx/error.log", identifier: "e.g. sshd" },
  fail: "A failure looks like",
  failHelp: "One pattern a line. Write <HOST> where the address is, and <USER> where the user name is.",
  ignore: "Never count lines like",
  ignoreHelp: "Optional. A line matching any of these never counts.",
  decoy: "This log is a decoy's",
  decoyHelp: "Nothing legitimate connects to a decoy, so whoever it catches is banned at high risk from the first time: held a day or longer, and blocked by other Weavian boxes too.",
  decoyNote: "It is a decoy, so whoever it catches is banned at high risk and blocked by other Weavian boxes too.",
  thresholds: (j) => `Ban an address after ${j.max_retry} failures within ${span(j.find_s)}, for ${span(j.ban_s)} or longer if it looks riskier.`,
  maxRetry: "Failures",
  findS: "Within",
  banS: "Banned for",
  test: "Try it on the last 200 lines",
  testing: "Trying…",
  tested: (n, of) => `${plural(n, "line")} of the last ${count(of)} would count as failures.`,
  testedNone: (of) => `None of the last ${count(of)} lines would count. Check the patterns, or wait until the log has a failure in it.`,
  ada: "Have Ada write it",
  adaQuestion: (src, about) => `Write a jail for ${src}${about ? ` that catches ${about}` : ""}: read its last lines with get_log_sample, write patterns that catch failed sign-ins and nothing else, test them with test_jail_filter, and propose the jail with propose_jail.`,
  save: "Save",
  cancel: "Cancel",
  presetSamples: "Lines it counts",
};

export const RECIDIVE = {
  title: "Repeat offenders",
  sentence: (r) => `An address banned ${r.bans} times in a week is banned for ${r.permanent ? "good" : "a month"}.`,
  off: "Off: each ban stands on its own, though a repeat ban still lasts twice as long as the one before.",
  bans: "Bans in a week",
  permanent: "Ban them for good, not for a month",
};

export const FAILING = {
  title: (n) => `Counting now: ${plural(n, "address", "addresses")}`,
  means: "Addresses failing here this week without being banned yet. The Weavian cloud compares them across sites, which is how slow guessing at many sites is caught.",
  cols: { address: "Address", week: "Failures this week", days: "Days", jails: "At" },
};

/* The fleet's shared list as this box holds it. */
export const FLEET = {
  title: "Shared across your sites and the Weavian fleet",
  means: "When one box bans an attacker, every box blocks it. Weavian's AI also spots addresses guessing slowly at many sites, which no single box would catch.",
  rows: {
    fleet: { what: "Banned at other sites", why: "Addresses other Weavian boxes banned with a high risk, or two sites banned." },
    model: { what: "Found by Weavian's AI", why: "Addresses failing a little at many sites over days, which looks like one slow attack." },
    feeds: { what: "Crowdsourced attacker lists", why: "Lists of attackers seen around the internet, which Weavian pulls for every box." },
  },
  entries: (n) => (n ? plural(n, "address", "addresses") : "none"),
  stopped: (n) => `${count(n)} stopped`,
  got: (iso) => (iso ? `Last updated ${at(iso)}.` : "Not received from the Weavian cloud yet."),
  noFeeds: "Not set up: a Weavian administrator turns them on.",
  never: "Never blocks this network, the Weavian cloud or your management networks.",
};

/* The caller's own address, banned at this site. */
export const YOU = {
  banned: (addr, b) => `Your own address, ${addr}, is banned at this site (${jailName(b.jail).toLowerCase()}, ${didWords(b)}). Until it ends, you cannot reach this box from where you are.`,
  canAsk: (to) => `Let yourself back in: we email a code${to ? ` to ${to}` : " to the address on your account"}.`,
  noEmail: "To let yourself back in, put an email on your account (your name, at the bottom left), or ask an operator to lift it here.",
  ask: "Email me a code",
  sent: (to) => `We emailed a code to ${to}. Type it here within 10 minutes.`,
  code: "Code",
  verify: "Let me back in",
  done: (addr) => `You are back in. This site will not ban ${addr} again for a day.`,
};

/* The Threat Intelligence screen. */
export const INTEL = {
  title: "Threat Intelligence",
  sharing: "Share bans across the fleet",
  sharingOn: "On: a high-risk ban at one box, or a ban at two, is blocked by every box.",
  sharingOff: "Off: every box blocks only its own bans. Turning it back on sends the list again.",
  offAsk: { title: "Stop sharing bans?", message: "Every box drops what other sites banned and the crowdsourced lists, at its next heartbeat. Its own bans stay.", confirmLabel: "Stop sharing" },
  model: "Weavian's AI: slow attacks across sites",
  modelMeans: "Every hour an Isolation Forest learns what the addresses failing at the fleet's boxes usually look like. An unusual one that failed at three sites or more on two days or more is listed for every box, for a week after it was last seen.",
  modelWaiting: (why) => `Learning: ${why}.`,
  modelLast: (m) => `Last ran ${at(m.at)} over ${plural(m.scored, "address", "addresses")}; ${plural(m.flagged, "address", "addresses")} listed now.`,
  top: "Most unusual last run",
  topCols: { address: "Address", score: "How unusual", sites: "Sites", days: "Days", listed: "Listed" },
  shared: "Shared now",
  sharedCols: { address: "Address", from: "From", sites: "Sites", until: "Until" },
  sharedNone: "Nothing is shared now.",
  abuse: "AbuseIPDB",
  abuseMeans: "Pulls the addresses AbuseIPDB's members report most, every six hours, and can report the fleet's own bans to it: an address, the kind of attack and how many tries, never whose box saw it.",
  crowd: "CrowdSec",
  crowdMeans: "Pulls a blocklist from a CrowdSec console integration every two hours. Make the integration in the CrowdSec console and paste its address, username and password here.",
  key: "API key",
  keyNew: "Paste the key",
  keyEnding: (e) => `Key ending ${e}`,
  pull: "Block what it lists",
  report: "Report our bans to it",
  confidence: "Only addresses at least this sure (25 to 100; below 100 needs a paid plan)",
  url: "Blocklist address",
  username: "Username",
  password: "Password",
  passwordKept: "Kept: type to replace",
  save: "Save",
  remove: "Remove",
  status: (s) => (s?.error ? `Last pull failed: ${s.error}` : s?.fetched ? `${plural(s.entries, "address", "addresses")}, pulled ${at(s.fetched)}` : "Not pulled yet"),
  unreadable: (why) => `The settings cannot be used: ${why}`,
  features: "What the model looks at",
};
